import { motion } from "framer-motion";
import "../../css/cogat/cogatExamples.css";


const keyAreas = [
    {
      title: "Non Verbal - Paper Folding",
      description: "Predicts the outcome of folding and cutting paper.",
      questions: [
        {
          q: "The paper in the top row(s) is folded and cut as shown. Which option in the bottom row is the result when the paper is unfolded?",
          options: { a: "Option A", b: "Option B", c: "Option C", d: "Option C" },
          correct: "D",
          explanation: "When the paper is folded, a single oval-shaped hole is punched. Upon unfolding, this results in two oval-shaped holes. Option A is incorrect as it includes four additional square cuts along the edges.",
          image: "/COGAT/NON-VERBAL Paper Folding/image31.png"
        }
      ],
    },
    {
      title: "Non Verbal - Figure Classification",
      description: "Groups figures based on shared characteristics.",
      questions: [
        {
          q: "Observe the shapes in the top row. They follow a specific pattern. Which shape in the bottom row best fits with the shapes in the top row?",
          correct: "C",
          explanation: "If you clearly observe, shapes in the top row has the star positioned at the bottom right of the shape. Option C has the same shape but is flipped in a different direction.",
          image: "/COGAT/NON-VERBAL Figure Classification/image26.png"
        }
      ],
    },
    {
      title: "Non Verbal - Figure Matrices",
      description: "Uses spatial reasoning to determine figure relationships in a matrix format.",
      questions: [
        {
          q: "Observe the shapes in the boxes on top. They follow a specific pattern. Which shape in the bottom row best fits with the question mark?",
          correct: "B",
          explanation: "The outer shape is missing in the top row. Option B matches the same pattern.",
          image: "/COGAT/NON-VERBAL Figure Matrices/image3.png"
        }
      ],
    },
    {
      title: "Quantitative - Number Analogies",
      description: "Identifies patterns and relationships between numbers.",
      questions: [
        {
          q: "Which picture on the bottom row best fits in the box with the question mark ?",
          correct: "C",
          explanation: "The correct number analogy in this example is that the number of shapes increases by one.",
          image: "/COGAT/QUANTITATIVE Number Analogies/image11.png"
        }
      ],
    },    
    {
      title: "Quantitative - Number Puzzles",
      description: "Solves puzzles using basic numerical logic.",
      questions: [
        {
          q: "Determine the number that replaces the question mark to make both sides of the equation equal ?",
          correct: "B",
          explanation: "Replacing '?' with 2 balances the left-hand side and right-hand side, making Option B the correct answer.",
          image: "/COGAT/QUANTITATIVE Number Puzzles/image18.png"
        }
      ],
    },
    {
      title: "Quantitative - Number Series",
      description: "Recognizes patterns in bead strings to determine the next in the series.",
      questions: [
        {
          q: "Which picture on the bottom row best fits to the series ?",
          correct: "A",
          explanation: "Each column in the top row follows a repeating pattern of 3 and 1. The next number in the sequence is 1, making Option A the correct answer.",
          image: "/COGAT/QUANTITATIVE Number Series/image1.png"
        }
      ],
    },
    {
      title: "Verbal - Picture Analogies",
      description: "Identifies relationships between images using a 2×2 matrix.",
      questions: [
        {
          q: "Which picture best completes the pattern in the box with the question mark ?",
          correct: "B",
          explanation: "The correct picture analogy here is that a light switch can turn a lamp on or off, just as car keys can start or stop a car. The key observation is that keys cannot turn on a house, making Option D an incorrect answer.",
          image: "/COGAT/VERBAL Picture Analogies/image10.png"
        }
      ],
    },
    {
      title: "Verbal - Sentence Completion",
      description: "Requires choosing the best picture that completes a sentence.",
      questions: [
        {
          q: "Emma went to the store to buy a ball, milk, an apple, and a cookie. She forgot to buy the cookie. Which picture shows what Emma bought at the store?",
          correct: "D",
          explanation: "The items in Option D correctly match the sentence - showing a ball, milk, and an apple (the cookie was forgotten, so it's not included).",
          image: "/COGAT/VERBAL Sentence Completion/image10.png"
        }
      ],
    }
  ];

export default function CogatExamples() {
  return (
    <section className="cogat-examples-section" aria-label="CoGAT Sample Questions">
      <div className="cogat-examples-container">
        <h2 className="cogat-examples-title">
          CoGAT Sample Questions & Practice Examples
        </h2>
        <p className="cogat-examples-subtitle">
          Practice with real CoGAT sample questions from all three batteries. Each example includes detailed explanations to help students understand the reasoning behind the correct answers.
        </p>

        {/* Sample Questions by Battery */}
        <div className="cogat-examples-grid">
          {keyAreas.map((area, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="cogat-example-card"
            >
              <h3 className="cogat-example-card-title">{area.title}</h3>
              <p className="cogat-example-card-description">{area.description}</p>

              {/* Sample Questions */}
              <div className="cogat-example-questions">
                {area.questions.map((item, qIndex) => (
                  <div key={qIndex} className="cogat-example-question-box">
                    <h4 className="cogat-example-question-text">{item.q}</h4>

                    {/* Display Image if available */}
                    {item.image && (
                      <div className="cogat-example-image-container">
                        <img 
                          src={`https://k12questionimages.s3.us-east-1.amazonaws.com${item.image}`} 
                          alt={`CoGAT sample question: ${area.title}`} 
                          className="cogat-example-image" 
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Answer and Explanation */}
                    <div className="cogat-example-answer-section">
                      <div className="cogat-answer-badge">
                        <span className="answer-label">Correct Answer:</span>
                        <span className="answer-value">{item.correct.toUpperCase()}</span>
                      </div>
                      <div className="cogat-explanation-box">
                        <strong className="explanation-label">Explanation:</strong>
                        <p className="explanation-text">{item.explanation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
